import React, { useState } from 'react';

import {
  BaseProgressPercentage,
  BaseProgressTrackerWrapper,
} from 'components/wrappers/BaseProgressBar';
import { StepValidationStatuses } from 'enums';
import BaseBulkImportStep, {
  BulkImportStagesModel,
} from 'components/wrappers/BaseBulkImportStep';
import ClientUploadSection from './ClientUploadSection/ClientUploadSection';
import ClientBulkImportTableSection from './ClientBulkImportTableSection';
import {
  BulkCustomerImportResponse,
  CreateCustomerRequest,
} from 'api/backofficeApiClient';
import { addIdsToValues } from 'utils/arrayUtils';
import { calculatePercent } from 'utils/trackerUtils';
import BulkImportResultModal, {
  BulkImportMode,
} from 'components/modals/BulkImportResultModal';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { createCustomers } from 'redux/reducers/clientSlice';
import { mapValuesToBulkImportResult } from 'utils/bulkImportUtils';

import styles from './ClientBulkImportPage.module.less';
import { CustomerBulkImportDetailsModel } from './ClientBulkImportTableSection/interface';

const prefixCls = 'ant-steps';
const iconPrefix = 'ant';

const initialStages: BulkImportStagesModel[] = [
  {
    title: 'Upload file',
    status: StepValidationStatuses.Process,
    stepIndex: 1,
    prefixCls,
    iconPrefix,
  },
  {
    title: 'File Processing',
    status: StepValidationStatuses.Wait,
    stepIndex: 2,
    prefixCls,
    iconPrefix,
  },
  {
    title: 'Complete import',
    status: StepValidationStatuses.Wait,
    stepIndex: 3,
    prefixCls,
    iconPrefix,
  },
];

export default function ClientBulkImportPage() {
  const [stages, setStages] = useState(initialStages);
  const [memberId, setMemberId] = useState<string>();
  const [isClientBulkImportModal, setIsClientBulkImport] = useState(false);
  const [customers, setCustomers] = useState<CustomerBulkImportDetailsModel[]>(
    [],
  );
  const [responseDetails, setResponseDetails] = useState<
    BulkCustomerImportResponse[]
  >([]);
  const dispatch = useAppDispatch();

  const isUploadSection = stages[0].status === StepValidationStatuses.Process;

  const onSetCustomers = async (
    customers: CreateCustomerRequest[],
    memberId: string,
  ) => {
    const customersWithIds = addIdsToValues(customers);

    setMemberId(memberId);
    setCustomers(customersWithIds);
    onSetStageStatus(0);
  };

  const onSetStageStatus = (changeStageIndex: number) => {
    setStages((prevState) => {
      return prevState.map((stage, index) => {
        if (index === changeStageIndex)
          return { ...stage, status: StepValidationStatuses.Finish };

        if (index === changeStageIndex + 1)
          return { ...stage, status: StepValidationStatuses.Process };

        return stage;
      });
    });
  };

  const handleClientBulkImportOpen = () => {
    setIsClientBulkImport(true);
  };

  const handleClientBulkImportClose = () => {
    setIsClientBulkImport(false);
    setStages(initialStages);
  };

  const handleSubmit = async (values: CreateCustomerRequest[]) => {
    if (!memberId) return;

    onSetStageStatus(1);
    const result = await dispatch(
      createCustomers({ models: values, memberId: memberId }),
    );

    if (createCustomers.fulfilled.match(result)) {
      setResponseDetails(result.payload);
    } else {
      setResponseDetails([]);
    }
    handleClientBulkImportOpen();
    onSetStageStatus(2);
  };

  return (
    <>
      {isClientBulkImportModal && (
        <BulkImportResultModal
          onClose={handleClientBulkImportClose}
          results={mapValuesToBulkImportResult(responseDetails)}
          mode={BulkImportMode.Customer}
        />
      )}
      <div className="page-wrapper scroll-box">
        <div className="page-holder">
          <div className={styles.holder}>
            <div className={styles.progressTracker}>
              <div className={styles.progressPercentage}>
                <BaseProgressPercentage percent={calculatePercent(stages)} />
              </div>

              <div className="page-box">
                <div className="page-inner-container">
                  <BaseProgressTrackerWrapper>
                    {stages.map((stage, index) => (
                      <BaseBulkImportStep
                        key={index}
                        stage={stage}
                        index={index}
                      />
                    ))}
                  </BaseProgressTrackerWrapper>
                </div>
              </div>
            </div>
          </div>

          {isUploadSection && (
            <ClientUploadSection onSuccess={onSetCustomers} />
          )}

          {!isUploadSection && (
            <ClientBulkImportTableSection
              prefilledCustomers={customers}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </div>
    </>
  );
}
